// src/pages/sales/purchase-order-components/PurchaseOrdersTabs.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import {
  Box,
  Paper,
  useTheme,
  alpha,
  Typography,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Container,
  CircularProgress
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
  LocalShipping as LocalShippingIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  RequestQuote as RequestQuoteIcon,
  NavigateNext as NavigateNextIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Import stage components
import OrderCreation from './OrderCreation';
import PICreation from './PICreation';
import DeliveryInformation from './DeliveryInformation';
import PISummary from './PISummary';

// Styled components
const SectionPaper = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 16,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

// Custom Timeline Connector
const TimelineConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    height: 4,
    border: 0,
    backgroundColor: '#e0e0e0',
    borderRadius: 2,
    transition: 'all 0.3s ease'
  }
}));

// Custom Timeline Step Icon
const TimelineStepIcon = styled('div')(({ theme, active, completed, stepIndex }) => {
  const colors = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main
  ];
  const bgColor = completed ? theme.palette.success.main : active ? colors[stepIndex] : '#e0e0e0';
  
  return {
    width: 56,
    height: 56,
    borderRadius: '50%',
    backgroundColor: bgColor,
    border: active ? `3px solid ${alpha(colors[stepIndex] || '#1976d2', 0.3)}` : '3px solid #f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: completed || active ? 'white' : '#999',
    transition: 'all 0.3s ease',
    position: 'relative',
    cursor: 'pointer',
    boxShadow: active ? `0 0 20px ${alpha(colors[stepIndex] || '#1976d2', 0.4)}` : '0 2px 8px rgba(0,0,0,0.1)',
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: `0 6px 20px ${alpha(bgColor || '#e0e0e0', 0.4)}`
    },
    '& .MuiSvgIcon-root': {
      fontSize: 28
    }
  };
});

// Timeline Component
function POTimeline({ activeStep, onStepClick, purchaseOrder, visibleSteps }) {
  const theme = useTheme();
  
  const allTimelineSteps = [
    {
      label: 'Order Creation',
      sublabel: 'Customer & Details',
      icon: <AssignmentIcon />,
      description: 'Select customer and order information',
      completed: purchaseOrder?.stage_completed?.includes('order_creation') || false,
      stepIndex: 0
    },
    {
      label: 'PI Creation',
      sublabel: 'Document Editor',
      icon: <ReceiptIcon />,
      description: 'Generate Proforma Invoice document',
      completed: purchaseOrder?.stage_completed?.includes('po_creation') || false,
      stepIndex: 1
    },
    {
      label: 'Delivery Info',
      sublabel: 'Shipping Details',
      icon: <LocalShippingIcon />,
      description: 'Configure delivery method and logistics',
      completed: purchaseOrder?.stage_completed?.includes('delivery_info') || false,
      stepIndex: 2
    },
    {
      label: 'Review',
      sublabel: 'Final Approval',
      icon: <CheckCircleIcon />,
      description: 'Review and approve proforma invoice',
      completed: purchaseOrder?.stage_completed?.includes('review') || false,
      stepIndex: 3
    }
  ];

  // Filter to only show steps user has permission to view
  const timelineSteps = allTimelineSteps.filter((_, index) => visibleSteps.includes(index));

  return (
    <Box sx={{ 
      p: 3,
      borderRadius: 4,
      mb: 3,
      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main', mb: 1 }}>
            Customer Order Workflow
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Track your progress through the order fulfillment process
          </Typography>
        </Box>
        <Box sx={{ 
          px: 3, 
          py: 1, 
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 2
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
            Step {visibleSteps.indexOf(activeStep) + 1} of {timelineSteps.length}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ 
        p: 3, 
        borderRadius: 3, 
        mb: 3, 
        background: 'white',
        border: `1px solid ${alpha(theme.palette.grey[400], 0.9)}`
      }}>
        <Stepper 
          activeStep={activeStep} 
          connector={<TimelineConnector />}
          sx={{ 
            '& .MuiStepLabel-root': {
              cursor: 'pointer'
            }
          }}
        >
          {timelineSteps.map((step, displayIndex) => {
            const isActive = step.stepIndex === activeStep;
            const isCompleted = step.completed;
            
            return (
              <Step key={step.label} onClick={() => onStepClick(step.stepIndex)}>
                <StepLabel
                  StepIconComponent={() => (
                    <TimelineStepIcon 
                      active={isActive} 
                      completed={isCompleted}
                      stepIndex={step.stepIndex}
                    >
                      {isCompleted ? <CheckCircleIcon /> : step.icon}
                    </TimelineStepIcon>
                  )}
                >
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500, 
                        color: isActive ? 'primary.main' : 'text.secondary',
                        mb: 0.5,
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {step.label}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: isActive ? 'primary.main' : 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1
                      }}
                    >
                      {step.sublabel}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        display: 'block',
                        maxWidth: 140,
                        lineHeight: 1.3
                      }}
                    >
                      {step.description}
                    </Typography>
                    
                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: isCompleted ? theme.palette.success.main : isActive ? theme.palette.warning.main : 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          px: 1.5,
                          py: 0.5,
                          backgroundColor: alpha(
                            isCompleted ? theme.palette.success.main : 
                            isActive ? theme.palette.warning.main : 
                            theme.palette.grey[500], 0.1
                          ),
                          borderRadius: 1,
                          border: `1px solid ${alpha(
                            isCompleted ? theme.palette.success.main : 
                            isActive ? theme.palette.warning.main : 
                            theme.palette.grey[500], 0.2
                          )}`,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        {isCompleted && <CheckCircleIcon sx={{ fontSize: 12 }} />}
                        {isActive && !isCompleted && <ScheduleIcon sx={{ fontSize: 12 }} />}
                        {isCompleted ? 'Completed' : isActive ? 'In Progress' : 'Pending'}
                      </Typography>
                    </Box>
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => {
            const currentVisibleIndex = visibleSteps.indexOf(activeStep);
            if (currentVisibleIndex > 0) {
              onStepClick(visibleSteps[currentVisibleIndex - 1]);
            }
          }}
          disabled={visibleSteps.indexOf(activeStep) === 0}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 500,
            '&:disabled': {
              borderColor: alpha(theme.palette.grey[400], 0.5)
            }
          }}
        >
          ← Previous Step
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {timelineSteps.map((step) => (
            <Box
              key={step.stepIndex}
              onClick={() => onStepClick(step.stepIndex)}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: step.stepIndex === activeStep ? 'primary.main' : alpha(theme.palette.grey[400], 0.5),
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.3)',
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}
            />
          ))}
        </Box>

        <Button
          variant="contained"
          onClick={() => {
            const currentVisibleIndex = visibleSteps.indexOf(activeStep);
            if (currentVisibleIndex < visibleSteps.length - 1) {
              onStepClick(visibleSteps[currentVisibleIndex + 1]);
            }
          }}
          disabled={visibleSteps.indexOf(activeStep) === visibleSteps.length - 1}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            fontWeight: 500,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            '&.Mui-disabled': {
              background: alpha(theme.palette.grey[400], 0.3),
              color: alpha(theme.palette.text.secondary, 0.5),
              border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`
            }
          }}
        >
          Next Step →
        </Button>
      </Box>
    </Box>
  );
}

const ProformaInvoicesTabs = () => {
  const { companyId, id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  
  // Check permissions for each step
  const canViewOrderCreation = hasPermission('sales.purchase_orders.order_creation.view', 'read');
  const canViewPOCreation = hasPermission('sales.purchase_orders.po_creation.view', 'read');
  const canViewDeliveryInfo = hasPermission('sales.purchase_orders.delivery_info.view', 'read');
  const canViewReview = hasPermission('sales.purchase_orders.review_info.view', 'read');

  // Build array of visible step indices
  const visibleSteps = [];
  if (canViewOrderCreation) visibleSteps.push(0);
  if (canViewPOCreation) visibleSteps.push(1);
  if (canViewDeliveryInfo) visibleSteps.push(2);
  if (canViewReview) visibleSteps.push(3);

  // Get first available step
  const firstAvailableStep = visibleSteps.length > 0 ? visibleSteps[0] : 0;
  
  const [activeTab, setActiveTab] = useState(firstAvailableStep);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const orderCreationRef = useRef(null);

  // Initialize PI - if "new", create temporary object like Formulations does!
  useEffect(() => {
    if (id === 'new') {
      // Create TEMPORARY PI object with NO ID - just like Formulations!
      const tempPO = {
        company_id: companyId,
        po_number: `PI-${Date.now()}`,
        status: 'draft',
        current_stage: 'order_creation',
        stage_completed: []
      };
      
      setPurchaseOrder(tempPO);
      
      // Auto-open dialog after short delay - JUST LIKE FORMULATIONS!
      setTimeout(() => {
        if (orderCreationRef.current && orderCreationRef.current.openEditDialog) {
          orderCreationRef.current.openEditDialog(true);
        }
      }, 100);
    } else if (id) {
      loadPO();
    }
  }, [id, companyId]);

  const loadPO = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customer_purchase_orders')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setPurchaseOrder(data);
    } catch (err) {
      console.error('Error loading PI:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (newPoId) => {
    // If we just created a new PI, navigate to its real ID
    if (id === 'new' && newPoId) {
      navigate(`/company/${companyId}/proforma-invoices/${newPoId}`, { replace: true });
      return;
    }
    
    // Reload the PI data to reflect updates
    if (newPoId) {
      loadPOById(newPoId);
    } else if (id && id !== 'new') {
      loadPO();
    } else if (purchaseOrder?.id) {
      // Force reload current PI to get updated stage_completed
      loadPOById(purchaseOrder.id);
    }
  };

  const loadPOById = async (poId) => {
    try {
      const { data, error } = await supabase
        .from('customer_purchase_orders')
        .select('*')
        .eq('id', poId)
        .single();

      if (error) throw error;
      setPurchaseOrder(data);
    } catch (err) {
      console.error('Error loading PI:', err);
    }
  };

  const handleStepClick = (stepIndex) => {
    // Only allow navigation to steps user has permission to view
    if (stepIndex >= 0 && stepIndex < 5 && visibleSteps.includes(stepIndex)) {
      setActiveTab(stepIndex);
    }
  };

  const handleBackToList = () => {
    navigate(`/company/${companyId}/proforma-invoices`);
  };

  const handleNavigateToDeliveryConfirmation = () => {
    if (purchaseOrder?.id && id !== 'new') {
      navigate(`/company/${companyId}/proforma-invoices/${purchaseOrder.id}/delivery-confirmation`);
    }
  };

  // Check if all visible steps are completed
  const areAllStepsCompleted = () => {
    if (!purchaseOrder?.id || id === 'new' || !purchaseOrder?.stage_completed) {
      return false;
    }

    const stageMapping = {
      0: 'order_creation',
      1: 'po_creation',
      2: 'delivery_info',
      3: 'review'
    };

    // Check if all visible steps are completed
    return visibleSteps.every(stepIndex => {
      const stageName = stageMapping[stepIndex];
      return purchaseOrder.stage_completed?.includes(stageName) || false;
    });
  };

  const renderStageComponent = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading Customer Order...
          </Typography>
        </Box>
      );
    }

    // For "new" orders or orders without ID, pass the temporary object and isCreatingNew flag
    const isCreatingNew = !purchaseOrder?.id;
    
    const handleNextStep = () => {
      const currentVisibleIndex = visibleSteps.indexOf(activeTab);
      if (currentVisibleIndex < visibleSteps.length - 1) {
        handleStepClick(visibleSteps[currentVisibleIndex + 1]);
      }
    };

    const handlePreviousStep = () => {
      const currentVisibleIndex = visibleSteps.indexOf(activeTab);
      if (currentVisibleIndex > 0) {
        handleStepClick(visibleSteps[currentVisibleIndex - 1]);
      }
    };

    const canGoNext = visibleSteps.indexOf(activeTab) < visibleSteps.length - 1;
    const canGoPrevious = visibleSteps.indexOf(activeTab) > 0;
    
    switch (activeTab) {
      case 0:
        return (
          <>
            <OrderCreation 
              ref={orderCreationRef}
              purchaseOrder={purchaseOrder || {}} 
              onUpdate={handleUpdate}
              isCreatingNew={isCreatingNew}
            />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 4,
              pt: 4,
              borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
            }}>
              <Button
                variant="outlined"
                onClick={handlePreviousStep}
                disabled={!canGoPrevious}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  '&:disabled': {
                    borderColor: alpha(theme.palette.grey[400], 0.5)
                  }
                }}
              >
                Previous Step
              </Button>
              <Button
                variant="contained"
                onClick={handleNextStep}
                disabled={!canGoNext}
                endIcon={<NavigateNextIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&.Mui-disabled': {
                    background: alpha(theme.palette.grey[400], 0.3),
                    color: alpha(theme.palette.text.secondary, 0.5),
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`
                  }
                }}
              >
                Next Step
              </Button>
            </Box>
          </>
        );
      case 1:
        return (
          <>
            <PICreation purchaseOrder={purchaseOrder || {}} onUpdate={handleUpdate} />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 4,
              pt: 4,
              borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
            }}>
              <Button
                variant="outlined"
                onClick={handlePreviousStep}
                disabled={!canGoPrevious}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  '&:disabled': {
                    borderColor: alpha(theme.palette.grey[400], 0.5)
                  }
                }}
              >
                Previous Step
              </Button>
              <Button
                variant="contained"
                onClick={handleNextStep}
                disabled={!canGoNext}
                endIcon={<NavigateNextIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&.Mui-disabled': {
                    background: alpha(theme.palette.grey[400], 0.3),
                    color: alpha(theme.palette.text.secondary, 0.5),
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`
                  }
                }}
              >
                Next Step
              </Button>
            </Box>
          </>
        );
      case 2:
        return (
          <>
            <DeliveryInformation purchaseOrder={purchaseOrder || {}} onUpdate={handleUpdate} />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 4,
              pt: 4,
              borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
            }}>
              <Button
                variant="outlined"
                onClick={handlePreviousStep}
                disabled={!canGoPrevious}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  '&:disabled': {
                    borderColor: alpha(theme.palette.grey[400], 0.5)
                  }
                }}
              >
                Previous Step
              </Button>
              <Button
                variant="contained"
                onClick={handleNextStep}
                disabled={!canGoNext}
                endIcon={<NavigateNextIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&.Mui-disabled': {
                    background: alpha(theme.palette.grey[400], 0.3),
                    color: alpha(theme.palette.text.secondary, 0.5),
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`
                  }
                }}
              >
                Next Step
              </Button>
            </Box>
          </>
        );
      case 3:
        return (
          <>
            <PISummary purchaseOrder={purchaseOrder || {}} onUpdate={handleUpdate} />
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 4,
              pt: 4,
              borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
            }}>
              <Button
                variant="outlined"
                onClick={handlePreviousStep}
                disabled={!canGoPrevious}
                startIcon={<ArrowBackIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  '&:disabled': {
                    borderColor: alpha(theme.palette.grey[400], 0.5)
                  }
                }}
              >
                Previous Step
              </Button>
              <Button
                variant="contained"
                onClick={handleNextStep}
                disabled={!canGoNext}
                endIcon={<NavigateNextIcon />}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 500,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&.Mui-disabled': {
                    background: alpha(theme.palette.grey[400], 0.3),
                    color: alpha(theme.palette.text.secondary, 0.5),
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`
                  }
                }}
              >
                Next Step
              </Button>
            </Box>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Container maxWidth="xl" sx={{ py: 3, pb: 12 }}>
        {/* Back to Orders */}
        <Box sx={{ mt: 5, mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToList}
            variant="text"
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              px: 1,
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
          >
            Back to Orders Page
          </Button>
        </Box>

        {/* Enhanced Page Header */}
        <Card sx={{
          borderRadius: 3,
          mb: 3,
          mt: 0,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: theme.palette.grey[100],
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, white)`,
            opacity: 0.8
          }
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 1 }}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 3, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                }}>
                  <RequestQuoteIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 500, 
                    color: 'primary.main', 
                    mb: 0.5,
                    fontSize: '2rem',
                    letterSpacing: '-0.5px'
                  }}>
                    {purchaseOrder?.po_number || 'New Proforma Invoice'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 500,
                    fontSize: '1rem'
                  }}>
                    Order Management
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {purchaseOrder?.id && id !== 'new' && areAllStepsCompleted() && (
                  <Button 
                    startIcon={<LocalShippingIcon />} 
                    onClick={handleNavigateToDeliveryConfirmation}
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      fontWeight: 500,
                      px: 3,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      }
                    }}
                  >
                    Delivery Confirmation
                  </Button>
                )}
              </Box>
            </Box>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary', 
              maxWidth: 600, 
              lineHeight: 1.5,
              fontSize: '0.95rem',
              fontWeight: 500
            }}>
              Create and manage customer orders with complete workflow tracking.
            </Typography>
          </CardContent>
        </Card>

        {/* Timeline Component */}
        <POTimeline 
          activeStep={activeTab} 
          onStepClick={handleStepClick}
          purchaseOrder={purchaseOrder}
          visibleSteps={visibleSteps}
        />

        {/* Main Content Area */}
        <SectionPaper>
          {renderStageComponent()}
        </SectionPaper>

        {/* Bottom Navigation Bar */}
        {purchaseOrder?.id && id !== 'new' && areAllStepsCompleted() && (
          <Card sx={{ 
            borderRadius: 3,
            mt: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)}, ${alpha(theme.palette.success.main, 0.02)})`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.15)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.success.main, 0.25)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: `linear-gradient(${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  }}>
                    <LocalShippingIcon sx={{ fontSize: 24, color: 'success.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 500, 
                      color: 'success.main', 
                      mb: 0.5
                    }}>
                      Ready for Delivery?
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 500
                    }}>
                      Complete the delivery confirmation workflow
                    </Typography>
                  </Box>
                </Box>
                <Button 
                  startIcon={<LocalShippingIcon />} 
                  onClick={handleNavigateToDeliveryConfirmation}
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: 2,
                    fontWeight: 500,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.4)}`
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Go to Delivery Confirmation
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default ProformaInvoicesTabs;